import { supabase } from '@/lib/supabase';
import type { Json } from '@/lib/database.types';
import { normalizeGridDataFormats, type GridDataFormats } from '@/shared/components/Grid/formats';
import type { SheetColumn, SheetRow, SheetTab } from './types';
export { publicSheetNoIndex, publicSheetPath } from './publicSheetContract';

export interface PublicSheetDocument {
  readonly id: string;
  readonly name: string;
  readonly columns: readonly SheetColumn[];
  readonly tabs: readonly SheetTab[];
  readonly rows: readonly SheetRow[];
  /** Column types, colours and borrowed lists, keyed by `sheet:<tabId>`. */
  readonly dataFormats: GridDataFormats;
  readonly activeTabId: string | null;
}

function columnsOf(value: Json): SheetColumn[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((item) => {
    if (!item || typeof item !== 'object' || Array.isArray(item)) return [];
    const record = item as Record<string, Json | undefined>;
    if (typeof record.id !== 'string' || typeof record.name !== 'string') return [];
    return [{ id: record.id, name: record.name, ...(typeof record.description === 'string' ? { description: record.description } : {}) }];
  });
}

export async function getPublicSheet(documentId: string): Promise<PublicSheetDocument | null> {
  const { data: document, error: documentError } = await supabase
    .from('sheet_documents')
    .select('id, columns, is_public, folders_nodes(name)')
    .eq('id', documentId)
    .eq('is_public', true)
    .maybeSingle();
  if (documentError) throw documentError;
  if (!document) return null;

  const { data: tabs, error: tabsError } = await supabase
    .from('sheet_tabs')
    .select('id, document_id, name, position, columns, is_hidden')
    .eq('document_id', documentId)
    .eq('is_hidden', false)
    .order('position');
  if (tabsError) throw tabsError;

  const { data: rows, error: rowsError } = await supabase
    .from('sheet_rows')
    .select('id, position, data, computed, tab_id')
    .eq('document_id', documentId)
    .order('position');
  if (rowsError) throw rowsError;

  // A signed-out visitor cannot read `grid_settings`; this returns the formats
  // and header overrides of THIS document and only while it is published.
  const [{ data: formats }, { data: headersData }, { data: notesData }] = await Promise.all([
    supabase.rpc('public_sheet_data_formats', { p_document: documentId }),
    supabase.rpc('public_sheet_headers', { p_document: documentId }),
    supabase.rpc('public_sheet_header_notes', { p_document: documentId }),
  ]);

  const recordOf = (value: Json | null) => (value && typeof value === 'object' && !Array.isArray(value)
    ? value
    : {}) as Record<string, Record<string, string>>;
  const headers = recordOf(headersData);
  const notes = recordOf(notesData);

  const name = Array.isArray(document.folders_nodes)
    ? document.folders_nodes[0]?.name ?? 'Sheet'
    : document.folders_nodes?.name ?? 'Sheet';
  const visibleTabs = (tabs ?? []).filter((tab) => !tab.is_hidden);
  const activeTab = visibleTabs[0];

  const resolveTabColumns = (tabColumns: SheetColumn[], tabId: string | null) => {
    const tabGridKey = tabId ? `sheet:${tabId}` : 'sheet';
    const tabOverrides = headers[tabGridKey] ?? headers['sheet'] ?? {};
    const tabNotes = notes[tabGridKey] ?? {};
    return tabColumns.map((col) => {
      const description = tabNotes[col.id] || col.description;
      return { ...col, name: tabOverrides[col.id] || col.name, ...(description ? { description } : {}) };
    });
  };

  const rawActiveColumns = activeTab ? columnsOf(activeTab.columns) : columnsOf(document.columns);
  const activeColumns = resolveTabColumns(rawActiveColumns, activeTab?.id ?? null);

  return {
    id: document.id,
    name,
    columns: activeColumns,
    tabs: visibleTabs.map((tab) => ({
      id: tab.id,
      documentId: tab.document_id,
      name: tab.name,
      position: Number(tab.position),
      columns: resolveTabColumns(columnsOf(tab.columns), tab.id),
      isHidden: tab.is_hidden,
    })),
    rows: (rows ?? []).map((row) => {
      const stored = (row.data && typeof row.data === 'object' && !Array.isArray(row.data) ? row.data : {}) as Record<string, unknown>;
      // A calculated column keeps nothing in `data`; its value is what the grid
      // last evaluated. Without this a public link shows the column empty.
      const computed = (row.computed && typeof row.computed === 'object' && !Array.isArray(row.computed) ? row.computed : {}) as Record<string, unknown>;
      const merged: Record<string, unknown> = { ...stored };
      for (const [field, value] of Object.entries(computed)) {
        if (value !== null && value !== undefined) merged[field] = value;
      }
      return {
        id: row.id,
        position: Number(row.position),
        tabId: row.tab_id,
        data: merged as Readonly<Record<string, unknown>>,
      };
    }),
    dataFormats: normalizeGridDataFormats(formats),
    activeTabId: activeTab?.id ?? null,
  };
}

export async function setSheetPublic(documentId: string, isPublic: boolean): Promise<boolean> {
  const { data, error } = await supabase.rpc('set_sheet_public', {
    p_document: documentId,
    p_is_public: isPublic,
  });
  if (error) throw error;
  return Boolean(data);
}




